import { useState, useEffect, useCallback, useMemo } from 'react'
import { RefreshCcw, AlertCircle, Check, X, History, ChevronRight, Search, Download } from 'lucide-react'
import { apiService } from '../services/api'

const OPERATORS = [
  { code: 'PLK', label: 'Plus (PLK)', table: 'plk_devices_sale.pos_data', column: 'kam_code' },
  { code: 'P4', label: 'Play (P4)', table: 'p4_devices_sale.pos_data', column: 'locon_kam' },
  { code: 'OPL', label: 'Orange (OPL)', table: 'orange_report.public.plants', column: 'locon_kam' },
]

const DIFF_LABELS = {
  mismatch: { label: 'Różnica', cls: 'bg-amber-100 text-amber-800' },
  app_set_op_clear: { label: 'App ma KAM, baza pusta', cls: 'bg-blue-100 text-blue-800' },
  app_clear_op_set: { label: 'App pusta, baza ma KAM', cls: 'bg-purple-100 text-purple-800' },
  app_only: { label: 'Tylko w aplikacji', cls: 'bg-emerald-100 text-emerald-800' },
  op_only: { label: 'Tylko w bazie operatora', cls: 'bg-rose-100 text-rose-800' },
}

const APPLIABLE = new Set(['mismatch', 'app_set_op_clear'])  // applied unconditionally
const APPLIABLE_OPTIONAL = new Set(['app_clear_op_set'])     // requires include_clear_op_set

export default function AdminKamSyncPage() {
  const [activeOperator, setActiveOperator] = useState(OPERATORS[0].code)
  const [view, setView] = useState('diff')          // 'diff' | 'history'
  const [diff, setDiff] = useState({ items: [], summary: {} })
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [filterTypes, setFilterTypes] = useState(new Set(Object.keys(DIFF_LABELS)))
  const [selected, setSelected] = useState(new Set())
  const [confirmModal, setConfirmModal] = useState(null)
  const [includeClearOpSet, setIncludeClearOpSet] = useState(false)
  const [note, setNote] = useState('')
  const [applyResult, setApplyResult] = useState(null)

  const loadDiff = useCallback(async () => {
    setLoading(true)
    setError(null)
    setSelected(new Set())
    try {
      const res = await apiService.getKamSyncDiff(activeOperator)
      setDiff({ items: res.data.items || [], summary: res.data.summary || {} })
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Błąd pobierania różnic')
    } finally {
      setLoading(false)
    }
  }, [activeOperator])

  const loadHistory = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiService.getKamSyncHistory({ operator: activeOperator, limit: 200 })
      setHistory(res.data.items || [])
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Błąd pobierania historii')
    } finally {
      setLoading(false)
    }
  }, [activeOperator])

  useEffect(() => {
    if (view === 'diff') loadDiff()
    else loadHistory()
  }, [view, loadDiff, loadHistory])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return diff.items.filter(d => {
      if (!filterTypes.has(d.diff_type)) return false
      if (!q) return true
      return [d.plant_code, d.salon_name, d.salon_city, d.app_kam_code, d.app_kam_name, d.op_kam_value]
        .some(v => (v || '').toString().toLowerCase().includes(q))
    })
  }, [diff.items, filterTypes, search])

  const selectableItems = useMemo(() => {
    return filtered.filter(d => APPLIABLE.has(d.diff_type) ||
      (includeClearOpSet && APPLIABLE_OPTIONAL.has(d.diff_type)))
  }, [filtered, includeClearOpSet])

  const allSelected = selectableItems.length > 0 &&
    selectableItems.every(d => selected.has(d.plant_code))

  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(selectableItems.map(d => d.plant_code)))
  }

  const toggleOne = (plantCode) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(plantCode)) next.delete(plantCode)
      else next.add(plantCode)
      return next
    })
  }

  const toggleType = (t) => {
    setFilterTypes(prev => {
      const next = new Set(prev)
      if (next.has(t)) next.delete(t)
      else next.add(t)
      return next
    })
  }

  const exportCsv = () => {
    const head = ['operator', 'plant_code', 'salon', 'city', 'app_kam_code', 'app_kam_name', 'op_kam_value', 'diff_type']
    const rows = [head.join(';')]
    for (const d of filtered) {
      rows.push([
        d.operator_code,
        d.plant_code,
        (d.salon_name || '').replaceAll(';', ','),
        (d.salon_city || '').replaceAll(';', ','),
        d.app_kam_code || '',
        (d.app_kam_name || '').replaceAll(';', ','),
        d.op_kam_value || '',
        d.diff_type,
      ].join(';'))
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kam-sync-${activeOperator}-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  const openConfirm = (mode) => {
    const plantCodes = mode === 'selected'
      ? Array.from(selected)
      : selectableItems.map(d => d.plant_code)
    if (plantCodes.length === 0) {
      alert('Brak wierszy do zatwierdzenia')
      return
    }
    setConfirmModal({ mode, plantCodes })
  }

  const doApply = async () => {
    if (!confirmModal) return
    setLoading(true)
    setError(null)
    try {
      const res = await apiService.applyKamSync({
        operator: activeOperator,
        plant_codes: confirmModal.plantCodes,
        include_clear_op_set: includeClearOpSet,
        note: note || null,
      })
      setApplyResult(res.data)
      setConfirmModal(null)
      setNote('')
      await loadDiff()
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Błąd zatwierdzania')
    } finally {
      setLoading(false)
    }
  }

  const opMeta = OPERATORS.find(o => o.code === activeOperator)

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <RefreshCcw className="w-8 h-8 text-blue-600" />
            Synchronizacja przypisań KAM
          </h1>
          <p className="text-gray-600 mt-1">
            Porównuje przypisania w aplikacji (Admin → Salony) z tabelami operatorów używanymi w raportach sprzedaży.
            Apply jest jednokierunkowy: aplikacja → tabela operatora.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setView('diff')}
            className={`px-3 py-2 rounded-lg text-sm ${view === 'diff' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            Różnice
          </button>
          <button
            onClick={() => setView('history')}
            className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm ${view === 'history' ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}
          >
            <History className="w-4 h-4" /> Historia
          </button>
        </div>
      </div>

      {/* Operator tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {OPERATORS.map(op => (
          <button
            key={op.code}
            onClick={() => setActiveOperator(op.code)}
            className={`px-4 py-2 text-sm border-b-2 -mb-px ${
              activeOperator === op.code
                ? 'border-blue-600 text-blue-700 font-semibold'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {op.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="text-sm">{error}</div>
        </div>
      )}

      {applyResult && (
        <div className="bg-green-50 border border-green-200 text-green-800 p-3 rounded-lg mb-4 flex items-start gap-2">
          <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            Zaktualizowano {applyResult.updated} rekordów, pominięto {applyResult.skipped}, błędów: {applyResult.failed}.
            <button onClick={() => setApplyResult(null)} className="ml-3 underline">OK</button>
          </div>
        </div>
      )}

      {view === 'diff' ? (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {Object.keys(DIFF_LABELS).map(type => (
              <button
                key={type}
                onClick={() => toggleType(type)}
                className={`p-3 rounded-lg border text-left transition ${
                  filterTypes.has(type)
                    ? 'bg-white border-blue-300 shadow-sm'
                    : 'bg-gray-50 border-gray-200 opacity-50'
                }`}
              >
                <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${DIFF_LABELS[type].cls}`}>
                  {DIFF_LABELS[type].label}
                </div>
                <div className="text-2xl font-bold text-gray-900 mt-2">
                  {diff.summary[type] || 0}
                </div>
              </button>
            ))}
          </div>

          {/* Toolbar */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Szukaj plant_code, nazwy, miasta, KAM-a..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeClearOpSet}
                onChange={e => setIncludeClearOpSet(e.target.checked)}
              />
              Czyść KAM w bazie gdy w app brak przypisania
            </label>
            <button
              onClick={loadDiff}
              className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
            >
              <RefreshCcw className="w-4 h-4" /> Odśwież
            </button>
            <button
              onClick={exportCsv}
              className="flex items-center gap-1 px-3 py-2 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
            >
              <Download className="w-4 h-4" /> CSV
            </button>
            <button
              onClick={() => openConfirm('selected')}
              disabled={selected.size === 0}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
            >
              Zatwierdź wybrane ({selected.size})
            </button>
            <button
              onClick={() => openConfirm('all')}
              disabled={selectableItems.length === 0}
              className="px-3 py-2 bg-blue-700 text-white rounded-lg text-sm disabled:opacity-50"
            >
              Zatwierdź wszystkie ({selectableItems.length})
            </button>
          </div>

          {/* Table */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              Brak różnic do pokazania.
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                    </th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plant code</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Salon</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">W aplikacji</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">W bazie operatora</th>
                    <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Typ różnicy</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 text-sm">
                  {filtered.map(d => {
                    const canSelect = APPLIABLE.has(d.diff_type) ||
                      (includeClearOpSet && APPLIABLE_OPTIONAL.has(d.diff_type))
                    const isSelected = selected.has(d.plant_code)
                    return (
                      <tr key={d.plant_code} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={!canSelect}
                            onChange={() => toggleOne(d.plant_code)}
                          />
                        </td>
                        <td className="px-3 py-2 font-mono text-gray-900">{d.plant_code}</td>
                        <td className="px-3 py-2">
                          <div className="text-gray-900 truncate max-w-xs" title={d.salon_name || ''}>
                            {d.salon_name || <span className="text-gray-400 italic">— (brak w app)</span>}
                          </div>
                          <div className="text-xs text-gray-500">{d.salon_city || ''}</div>
                        </td>
                        <td className="px-3 py-2">
                          {d.app_kam_code ? (
                            <span className="inline-block px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-mono text-xs">
                              {d.app_kam_code}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">— brak —</span>
                          )}
                          {d.app_kam_name && (
                            <div className="text-xs text-gray-500 mt-0.5">{d.app_kam_name}</div>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          {d.op_kam_value ? (
                            <span className="inline-block px-2 py-0.5 rounded bg-rose-100 text-rose-800 font-mono text-xs">
                              {d.op_kam_value}
                            </span>
                          ) : (
                            <span className="text-gray-400 italic text-xs">— brak —</span>
                          )}
                        </td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs ${DIFF_LABELS[d.diff_type]?.cls || 'bg-gray-100 text-gray-800'}`}>
                            {DIFF_LABELS[d.diff_type]?.label || d.diff_type}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="text-xs text-gray-500 mt-2">
            Tabela docelowa: <span className="font-mono">{opMeta?.table}</span>, kolumna <span className="font-mono">{opMeta?.column}</span>.
            Apply UPDATE-uje tylko wartość kolumny KAM. Salony „tylko w app" lub „tylko w bazie operatora" są pomijane (wymagają osobnego procesu importowego).
          </div>
        </>
      ) : (
        <HistoryView items={history} loading={loading} />
      )}

      {confirmModal && (
        <ConfirmModal
          info={confirmModal}
          opMeta={opMeta}
          note={note}
          setNote={setNote}
          includeClearOpSet={includeClearOpSet}
          loading={loading}
          onCancel={() => setConfirmModal(null)}
          onConfirm={doApply}
        />
      )}
    </div>
  )
}

function ConfirmModal({ info, opMeta, note, setNote, includeClearOpSet, loading, onCancel, onConfirm }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <ChevronRight className="w-5 h-5 text-blue-600" />
            Potwierdź synchronizację
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3 text-sm">
          <p className="text-gray-700">
            Zostanie wykonane <span className="font-mono">UPDATE</span> w tabeli <span className="font-mono">{opMeta?.table}</span> dla{' '}
            <span className="font-bold">{info.plantCodes.length}</span> rekordów.
            Wartość w kolumnie <span className="font-mono">{opMeta?.column}</span> zostanie ustawiona zgodnie ze stanem w aplikacji.
          </p>
          {includeClearOpSet && (
            <p className="text-amber-700 text-xs">
              Włączona opcja: czyszczenie wartości w bazie dla salonów bez przypisania w app.
            </p>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Notatka (opcjonalna, trafia do audit log)</label>
            <input
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="np. „przepięcie Mirek → Monika po rotacji”"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              maxLength={2000}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onCancel} className="px-4 py-2 bg-gray-100 rounded-lg text-sm">Anuluj</button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50"
          >
            {loading ? 'Aplikuję...' : 'Zatwierdź'}
          </button>
        </div>
      </div>
    </div>
  )
}

function HistoryView({ items, loading }) {
  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }
  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
        Brak wpisów w historii synchronizacji dla tego operatora.
      </div>
    )
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kiedy</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Operator</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plant</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Z</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Na</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Akcja</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kto</th>
            <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notatka</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {items.map(it => (
            <tr key={it.id} className="hover:bg-gray-50">
              <td className="px-3 py-2 text-gray-700 whitespace-nowrap">
                {new Date(it.performed_at).toLocaleString('pl-PL')}
              </td>
              <td className="px-3 py-2 font-mono">{it.operator_code}</td>
              <td className="px-3 py-2 font-mono">{it.plant_code}</td>
              <td className="px-3 py-2">
                {it.before_value
                  ? <span className="font-mono text-rose-700">{it.before_value}</span>
                  : <span className="text-gray-400 italic">brak</span>}
              </td>
              <td className="px-3 py-2">
                {it.after_value
                  ? <span className="font-mono text-emerald-700">{it.after_value}</span>
                  : <span className="text-gray-400 italic">brak</span>}
              </td>
              <td className="px-3 py-2">{it.action}</td>
              <td className="px-3 py-2">{it.actor_name}</td>
              <td className="px-3 py-2 text-gray-600 max-w-xs truncate" title={it.note || ''}>
                {it.note || ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
